import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import throttle from 'lodash.throttle'
import { Voice } from '@web-lite/voice-recorder/build/main/lib/types'
import { WithBodyMessage } from '@weblite/ts-action/message'
import { styled } from '@mui/material/styles'
import { closeEmojiPicker } from 'weblite-web-emoji'
import { deleteDraft, setDraft } from '$logic/draft'
import { getOpenedRoomId } from '$logic/opened-room'
import {
  editMessageHandler,
  forwardMessages,
  sendNewMessage,
  sendVoiceMessage,
} from '$logic/message'
import { messageTypeByFileType } from '$func/file'
import { sendMessageAction } from '$logic/message-action'
import { RECEIVE_ACTION_TIME_OUT } from '$logic/message-action/events'
import { dispatchSetSendFieldHeight } from '$comp/main/root.action'
import { sendFieldHeightView } from '$comp/main/root.reducer'
import { useRoomAttribute } from '$logic/room/react-hook'
import { areEqualStr } from '$func/text'
import { showDiscardForwardConfirmModal } from '$comp/modals/confirmation-dialog'

import { MentionMenu } from './components/mention-menu'
import { TextPreview } from './components/text-preview'
import { editorAPI } from './components/text-editor'
import { Edit } from './components/edit'
import { Reply } from './components/reply'
import { ForwardPreview } from './components/forward'
import { UploadPreview } from './components/upload-preview'

import { SendFieldBackdrop } from './components/backdrop'
import { VoiceRecorder } from './components/voice-recorder'
import {
  ImagePreviewButtonBar,
  VideoPreviewButtonBar,
} from './components/preview-button-bar'
import {
  DEFAULT_SEND_FIELD_HEIGHT,
  ImagePreviewTypes,
  MEDIA_PREVIEW_BUTTON_BAR_HEIGHT,
  RecorderStatus,
  SendFieldAction,
  VideoPreviewTypes,
} from './send-field.type'
import { useDraft } from './use-draft'
import { nothing } from '$func/utils'

interface SendFieldProps {
  fullHeight: boolean
}

const Root = styled('div', {
  shouldForwardProp: (prop: string) => !['fullHeight'].includes(prop),
})<SendFieldProps>(({ fullHeight }) => ({
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  position: 'absolute',
  bottom: '0',
  width: '100%',
  height: fullHeight ? '100%' : 'unset',
  zIndex: 4,
}))

export const sendFieldApis = {
  getRepliedTo() {
    return nothing()
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setEditContent(_message: WithBodyMessage) {
    nothing()
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setReplyContent(_messageIid: number) {
    nothing()
  },

  setForwardContent(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _roomId: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _messagesIids: number[],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _publicHall: boolean,
  ) {
    nothing()
  },

  reset() {
    nothing()
  },

  resetAction() {
    nothing()
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setFile(_file: File) {
    nothing()
  },
  checkVoiceStatus(): Promise<boolean> {
    return Promise.resolve(true)
  },
}

export const SendField = memo(function({ roomId }: { roomId: string }) {
  const sendFieldRef = useRef(null)
  const mentionMenuRef = useRef<HTMLDivElement>(null)

  const roomType = useRoomAttribute('type', roomId)
  const mentionIsDisabled = roomType !== 'CHANNEL' && roomType !== 'GROUP'

  const height = useSelector(sendFieldHeightView)

  const [file, setFile] = useState<File>()
  const [isFullHeight, setIsFullHeight] = useState<boolean>(false)

  const [mentionQuery, setMentionQuery] = useState<string>()
  const [action, setAction] = useState<SendFieldAction>({ type: 'NEW_MESSAGE' })
  const [recorderState, setRecorderState] = useState<RecorderStatus>('IDLE')
  const recorderStateRef = useRef<RecorderStatus>('IDLE')

  const [videoCompressionHeight, setVideoCompressionHeight] = useState<number>()
  const synchronizeHeight = useCallback(() => {
    if (!sendFieldRef.current) return

    const { clientHeight } = sendFieldRef.current
    const mentionMenuHeight = mentionMenuRef.current?.clientHeight || 0
    const newHeight = clientHeight - mentionMenuHeight
    if (
      (height !== newHeight && !isFullHeight) ||
      newHeight === DEFAULT_SEND_FIELD_HEIGHT
    ) {
      dispatchSetSendFieldHeight(newHeight)
    }
  }, [height, isFullHeight])

  useEffect(() => {
    const fullHeight =
      !!file ||
      (action.type === 'EDIT' &&
        ['IMAGE', 'VIDEO'].includes(action.message?.type))

    setIsFullHeight(fullHeight)
    if (!fullHeight) synchronizeHeight()
  }, [file, action, synchronizeHeight])

  const resetSendFieldAction = useCallback(() => {
    setAction({ type: 'NEW_MESSAGE' })
  }, [])

  const resetRecorder = useCallback(() => {
    setRecorderState('IDLE')
  }, [])

  const resetSendField = useCallback(() => {
    editorAPI.setText('')
    resetSendFieldAction()
    resetRecorder()
    setFile(undefined)
  }, [resetRecorder, resetSendFieldAction])

  const onTypingEvent = useCallback(
    throttle(
      () => sendMessageAction('TYPING', roomId),
      RECEIVE_ACTION_TIME_OUT,
      { trailing: false },
    ),
    [roomId],
  )

  const { onDraftChange } = useDraft({
    roomId,
    onTypingEvent,
    action,
    setAction,
  })

  const onEditorTextChange = useCallback(
    (text: string) => {
      onDraftChange(text)
      synchronizeHeight()
    },
    [onDraftChange, synchronizeHeight],
  )

  const submitSendField = useCallback(async () => {
    closeEmojiPicker()
    const body = editorAPI.getText()?.trim()
    if (!file && !body && action.type !== 'FORWARD') return

    if (editorAPI.keepFocus) {
      editorAPI.setKeepFocus(false)
      editorAPI.focus()
    }

    deleteDraft(getOpenedRoomId())
    resetSendField()

    setTimeout(async () => {
      if (action.type === 'EDIT') {
        const bodyChanged = !areEqualStr(body, action.message.body)
        if (!bodyChanged && !file) return

        const updatedFields = {
          ...(bodyChanged && { body }),
          ...(file && { file }),
        }
        editMessageHandler(
          roomId,
          action.message.iid,
          action.message.type,
          updatedFields.body,
          updatedFields.file,
        )
      } else {
        const repliedTo = action.type === 'REPLY' ? action.iid : undefined
        await sendNewMessage(
          file,
          body,
          repliedTo,
          roomId,
          roomType,
          videoCompressionHeight,
        )
        if (action.type === 'FORWARD') {
          const { roomId: sourceRoomId, iids: sourceIids } = action.forward
          analite('MESSAGE_FORWARD', {
            multiple: sourceIids.length > 1,
            source: 'SEND_FIELD',
          })
          forwardMessages(
            [roomId],
            {
              roomId: sourceRoomId,
              iids: sourceIids,
            },
            undefined,
          )
        }
      }
    }, 0)
  }, [action, file, roomId, videoCompressionHeight, resetSendField])

  const setFileContent = useCallback(
    (fileContent: File) => {
      if (recorderState !== 'IDLE') return
      if (action.type === 'EDIT') resetSendField()
      setFile(fileContent)
      editorAPI.focus()
    },
    [resetSendField, action.type, recorderState],
  )

  const checkIsRecordingVoice = async () => {
    return false // TODO: Implement me
    if (
      recorderStateRef.current === 'PRESSED' ||
      recorderStateRef.current === 'RECORD'
    ) {
      resetRecorder()
      // TODO: Implement me
      // showDiscardRecordingVoiceConfirmModal()
      return true
    }
    return false
  }

  const sendVoice = useCallback(
    (voice: Voice) => {
      sendVoiceMessage(
        voice,
        undefined,
        action.type === 'REPLY' ? action.iid : undefined,
        roomId,
      )
      analite('CHAT_SEND_MESSAGE', { type: 'VOICE', chatType: roomType })

      resetSendField()
    },
    [action, roomId, resetSendField],
  )

  const cancelReply = useCallback(() => {
    resetSendFieldAction()
    const body = editorAPI.getText() || ''
    if (body) setDraft(roomId, { body, type: 'NEW_MESSAGE' })
    else deleteDraft(roomId)
  }, [resetSendFieldAction, roomId])

  const cancelEdit = useCallback(() => {
    editorAPI.setText('')
    resetSendFieldAction()
    deleteDraft(getOpenedRoomId())
  }, [resetSendFieldAction])

  const setReplyContent = useCallback((messageIid: number) => {
    editorAPI.focus()
    setAction({ type: 'REPLY', iid: messageIid })
  }, [])

  const setForwardContent = useCallback(
    async (
      sourceRoomId: string,
      messagesIids: number[],
      publicHall: boolean,
    ) => {
      resetSendField()
      setAction({
        type: 'FORWARD',
        forward: { iids: messagesIids, roomId: sourceRoomId, publicHall },
      })
    },
    [resetSendField],
  )

  const setEditContent = useCallback((message: WithBodyMessage) => {
    editorAPI.focus()
    editorAPI.setText(message.body || '')
    setAction({ type: 'EDIT', message })
  }, [])

  const getRepliedTo = useCallback(() => {
    if (action.type !== 'REPLY') return null
    return action.iid
  }, [action])

  useEffect(() => {
    sendFieldApis.setEditContent = setEditContent
    sendFieldApis.setReplyContent = setReplyContent
    sendFieldApis.setForwardContent = setForwardContent
    sendFieldApis.reset = resetSendField
    sendFieldApis.resetAction = resetSendFieldAction
    sendFieldApis.setFile = setFileContent
    sendFieldApis.checkVoiceStatus = checkIsRecordingVoice
    sendFieldApis.getRepliedTo = getRepliedTo
  }, [setEditContent, setReplyContent, setForwardContent, resetSendField, resetSendFieldAction, setFileContent, getRepliedTo])

  const recorderInterval: any = useRef()

  useEffect(() => {
    recorderStateRef.current = recorderState
    if (recorderState === 'PRESSED') {
      sendMessageAction('RECORD_VOICE', roomId)

      recorderInterval.current = setInterval(
        () => sendMessageAction('RECORD_VOICE', roomId),
        RECEIVE_ACTION_TIME_OUT,
      )
    }

    if (
      recorderState === 'WAITING' ||
      recorderState === 'PLAYING' ||
      recorderState === 'IDLE'
    )
      clearInterval(recorderInterval.current)
  }, [recorderState])

  useEffect(
    () => () => {
      dispatchSetSendFieldHeight(DEFAULT_SEND_FIELD_HEIGHT)
      clearInterval(recorderInterval.current)
    },
    [],
  )

  const [imagePreviewMode, setImagePreviewMode] = useState<ImagePreviewTypes>(
    'comment',
  )
  const [videoPreviewMode, setVideoPreviewMode] = useState<VideoPreviewTypes>(
    'comment',
  )

  const showText = recorderState === 'IDLE' || recorderState === 'PRESSED'
  // when previewing image in non comment status

  const fileType = file && file.type
  const imageCaptionMode =
    (fileType && messageTypeByFileType(fileType) === 'IMAGE') ||
    (action.type === 'EDIT' && action.message?.type === 'IMAGE') ||
    (fileType && messageTypeByFileType(fileType) === 'VIDEO') ||
    (action.type === 'EDIT' && action.message?.type === 'VIDEO')

  const showImagePreviewButtonsBar =
    file &&
    action.type !== 'EDIT' &&
    messageTypeByFileType(file.type) === 'IMAGE' &&
    imagePreviewMode === 'comment'

  const showVideoPreviewButtonsBar =
    file &&
    action.type !== 'EDIT' &&
    messageTypeByFileType(file.type) === 'VIDEO' &&
    videoPreviewMode === 'comment'

  const textPreviewIsHidden =
    file &&
    action.type !== 'EDIT' &&
    (imagePreviewMode !== 'comment' || videoPreviewMode !== 'comment')

  return (
    <Root dir="rtl" ref={sendFieldRef} fullHeight={isFullHeight}>
      {file &&
        messageTypeByFileType(file.type) !== 'IMAGE' &&
        messageTypeByFileType(file.type) !== 'VIDEO' && (
          <>
            <SendFieldBackdrop />
            <div style={{ height: '100%' }} />
          </>
        )}

      {mentionQuery !== undefined && !imageCaptionMode && (
        <div ref={mentionMenuRef} style={{ zIndex: 1 }}>
          <MentionMenu query={mentionQuery} />
        </div>
      )}

      {action.type === 'EDIT' && (
        <Edit
          message={action.message as WithBodyMessage}
          onCancel={cancelEdit}
          setSelectedFile={setFile}
          newFile={file}
        />
      )}

      {action.type === 'REPLY' &&
        !(file && messageTypeByFileType(file.type) === 'IMAGE') &&
        !(file && messageTypeByFileType(file.type) === 'VIDEO') && (
          <Reply
            roomId={roomId}
            messageIid={action.iid}
            onCancel={cancelReply}
          />
        )}
      {action.type === 'FORWARD' && (
        <ForwardPreview
          roomId={action.forward.roomId}
          iids={action.forward.iids}
          onCancel={() =>
            showDiscardForwardConfirmModal(
              action.forward.roomId,
              action.forward.iids.length,
              cancelReply,
            )
          }
        />
      )}

      <VoiceRecorder
        sendVoice={sendVoice}
        recorderState={recorderState}
        setRecorderState={setRecorderState}
      />

      {file && action.type !== 'EDIT' && (
        <UploadPreview
          file={file}
          onCancel={() => {
            setFile(undefined)
            setImagePreviewMode('comment')
            setVideoPreviewMode('comment')
            setVideoCompressionHeight(undefined)
          }}
          hasBorder={action.type !== 'REPLY'}
          imagePreviewMode={imagePreviewMode}
          videoPreviewMode={videoPreviewMode}
          setImagePreviewMode={setImagePreviewMode}
          setVideoPreviewMode={setVideoPreviewMode}
          setFile={setFile}
          videoCompressionHeight={videoCompressionHeight}
          setVideoCompressionHeight={setVideoCompressionHeight}
        />
      )}

      {(showText || (file && action.type === 'EDIT')) && (
        <TextPreview
          hasFile={!!file}
          action={action}
          mentionQuery={mentionQuery}
          imageCaptionMode={imageCaptionMode}
          mentionIsDisabled={mentionIsDisabled}
          recorderState={recorderState}
          setRecorderState={setRecorderState}
          resetSendFieldAction={resetSendFieldAction}
          resetSendField={resetSendField}
          submitSendField={submitSendField}
          onEditorTextChange={onEditorTextChange}
          setSelectedFile={setFileContent}
          setMentionQuery={setMentionQuery}
          bottomOffset={
            showImagePreviewButtonsBar || showVideoPreviewButtonsBar
              ? MEDIA_PREVIEW_BUTTON_BAR_HEIGHT
              : 0
          }
          isHidden={Boolean(textPreviewIsHidden)}
          roomType={roomType}
        />
      )}

      {showImagePreviewButtonsBar && (
        <ImagePreviewButtonBar setImagePreviewMode={setImagePreviewMode} />
      )}
      {showVideoPreviewButtonsBar && (
        <VideoPreviewButtonBar setVideoPreviewMode={setVideoPreviewMode} />
      )}
    </Root>
  )
})
